import { CircleMarker, MapContainer, Popup, TileLayer } from "react-leaflet";
import "leaflet/dist/leaflet.css";

import { LA_MAP_CENTER, STATION_COORDS } from "../constants";
import type { SimulationState, StationId, VehicleAgent } from "../types";

interface SimulationMapProps {
  simulation: SimulationState;
}

const stateColor: Record<VehicleAgent["currentState"], string> = {
  idle: "#475569",
  driving: "#0284c7",
  waiting_for_charge: "#f59e0b",
  charging: "#059669",
};

const stationLabel: Record<StationId, string> = {
  stationA: "Station A",
  stationB: "Station B",
};

const interpolateCoord = (
  from: [number, number],
  to: [number, number],
  progress: number,
): [number, number] => {
  const p = Math.max(0, Math.min(1, progress));
  return [from[0] + (to[0] - from[0]) * p, from[1] + (to[1] - from[1]) * p];
};

const getVehicleCoord = (vehicle: VehicleAgent): [number, number] => {
  const home = STATION_COORDS[vehicle.homeStationId];

  if (vehicle.position.zone === "road") {
    const trip = vehicle.trips.find((item) => item.id === vehicle.activeTripId);
    const destination = trip?.destinationCoord;
    if (destination) {
      const progress = Math.max(0, Math.min(1, vehicle.position.roadProgress || 0));
      if (progress <= 0.5) {
        return interpolateCoord(home, destination, progress * 2);
      }
      return interpolateCoord(destination, home, (progress - 0.5) * 2);
    }
  }

  const offsetBucket = Number(vehicle.id.split("-")[1] || "1");
  const latOffset = ((offsetBucket % 5) - 2) * 0.0015;
  const lngOffset = ((offsetBucket % 4) - 1.5) * 0.0017;

  if (vehicle.position.stationId) {
    const stationCoord = STATION_COORDS[vehicle.position.stationId];
    return [stationCoord[0] + latOffset, stationCoord[1] + lngOffset];
  }

  return [home[0] + latOffset, home[1] + lngOffset];
};

const VehicleMarker = ({ vehicle }: { vehicle: VehicleAgent }) => {
  const coord = getVehicleCoord(vehicle);
  const socPct = (vehicle.socKWh / vehicle.batteryCapacityKWh) * 100;
  const activeTrip = vehicle.trips.find((item) => item.id === vehicle.activeTripId);

  return (
    <CircleMarker center={coord} radius={7} pathOptions={{ color: stateColor[vehicle.currentState], fillOpacity: 0.8 }}>
      <Popup>
        <div className="text-xs">
          <p className="font-semibold">{vehicle.id}</p>
          <p>State: {vehicle.currentState}</p>
          <p>SOC: {socPct.toFixed(1)}%</p>
          <p>Home: {stationLabel[vehicle.homeStationId]}</p>
          {activeTrip && (
            <p>
              Destination: {activeTrip.destinationCoord[0].toFixed(3)}, {activeTrip.destinationCoord[1].toFixed(3)}
            </p>
          )}
        </div>
      </Popup>
    </CircleMarker>
  );
};

export default function SimulationMap({ simulation }: SimulationMapProps) {
  return (
    <div className="w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-200 px-4 py-3">
        <h3 className="text-base font-semibold text-slate-800">Los Angeles Simulation Map</h3>
        <p className="text-xs text-slate-500">Vehicle location, SOC, and station charger usage in real time.</p>
      </div>
      <div className="h-[420px] w-full">
        <MapContainer center={LA_MAP_CENTER} zoom={11} scrollWheelZoom className="h-full w-full">
          <TileLayer
            attribution="Tiles &copy; Esri"
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}"
          />

          {(Object.keys(simulation.stations) as StationId[]).map((stationId) => {
            const station = simulation.stations[stationId];
            const coord = STATION_COORDS[stationId];
            const currentDemand = station.demandProfileKw[station.demandProfileKw.length - 1] || 0;

            return (
              <CircleMarker
                key={stationId}
                center={coord}
                radius={13}
                pathOptions={{ color: "#7c3aed", fillColor: "#a78bfa", fillOpacity: 0.8 }}
              >
                <Popup>
                  <div className="text-xs">
                    <p className="font-semibold">{stationLabel[stationId]}</p>
                    <p>Charging: {station.chargingVehicleIds.length} / {station.chargers.length}</p>
                    <p>Queue: {station.waitingQueue.length}</p>
                    <p>Demand: {currentDemand} kW</p>
                  </div>
                </Popup>
              </CircleMarker>
            );
          })}

          {simulation.vehicles.map((vehicle) => (
            <VehicleMarker key={vehicle.id} vehicle={vehicle} />
          ))}
        </MapContainer>
      </div>
    </div>
  );
}
